import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onBeforeMount } from "vue";
import { BookingService } from "@/services/BookingService";
import router from "@/router";
import { differenceInCalendarDays, parseISO } from "date-fns";
import BusinessProcessActivityTimeline from "@/components/activity/BusinessProcessActivityTimeline.vue";
import CustomerBookingVersionHistory from "@/components/Bookings/customer/CustomerBookingVersionHistory.vue";
import ResourceErrorState from "@/components/layout/ResourceErrorState.vue";
import { formatLocation } from "@/utils/formatLocation";
import { useResourceState } from "@/services/useResourceState";


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerBookingDetailsView',
  setup(__props, { expose: __expose }) {
  __expose();

const booking = ref<any>({});
const bookingsService = new BookingService();
const bookingVersions = ref<any>([]);
const bookingId = router.currentRoute.value.params.id as string;
const { loading, error, runLoad } = useResourceState();

// Loaded here rather than with a top-level await: an async setup() has no way to report a
// failure, so a booking the user cannot reach used to take the whole view down with it.
const fetchData = () =>
  runLoad(async () => {
    booking.value = await bookingsService.get(bookingId);
    bookingVersions.value = await bookingsService.getVersions(bookingId);
  });

onBeforeMount(fetchData);

// ---------- UI helpers ----------

const STATUS_UI: Record<string, { label: string; color: string }> = {
  BOOKED: { label: "Lefoglalva", color: "blue" },
  COMPLETED: { label: "Teljesítve", color: "green" },
  CANCELLED: { label: "Törölve", color: "red" },
};

const statusUi = (status: string) =>
  STATUS_UI[status] ?? { label: status, color: "grey" };

const formatDateTime = (timestamp?: number) => {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleString("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPrice = (value?: number) => {
  if (!value) return "-";
  return `${value.toLocaleString("hu-HU")} Ft`;
};

// ---------- Table config ----------

const itemHeaders = [
  { title: "Szállás", key: "accommodation" },
  { title: "Termék", key: "product" },
  { title: "Érkezés", key: "startDate" },
  { title: "Távozás", key: "endDate" },
  { title: "Létszám", key: "numberOfPeople", align: "end" },
  { title: "Nettó /fő/éj", key: "retailPrice", align: "end" },
  {
    title: "Bruttó /fő/éj",
    key: "outgoingGrossPerPersonPerNight",
    align: "end",
  },
];

const totalOutgoingPrice = computed(() => {
  if (!booking.value.items) return 0;
  return booking.value.items.reduce((sum: number, item: any) => {
    const start = parseISO(item.startDate);
    const end = parseISO(item.endDate);
    const numberOfNights = differenceInCalendarDays(end, start);
    return sum + numberOfNights * item.numberOfPeople * item.retailPrice;
  }, 0);
});

const formatPublicReference = (ref: string) => {
  if (!ref) return "";
  return ref.match(/.{1,4}/g)!.join("-");
};

const __returned__ = { booking, bookingsService, bookingVersions, bookingId, loading, error, runLoad, fetchData, STATUS_UI, statusUi, formatDateTime, formatPrice, itemHeaders, totalOutgoingPrice, formatPublicReference, ref, computed, onBeforeMount, get BookingService() { return BookingService }, get router() { return router }, get differenceInCalendarDays() { return differenceInCalendarDays }, get parseISO() { return parseISO }, BusinessProcessActivityTimeline, CustomerBookingVersionHistory, ResourceErrorState, get formatLocation() { return formatLocation }, get useResourceState() { return useResourceState } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})